import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useQueriesCollection } from '@src/queries/collections/useQueryCollection';
import { useQueryCollections } from '@src/queries/collections/useQueryCollections';
import { useQueryGroupMentionPages } from '@src/queries/users/useQueryGroupMentionPages';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useQueryBlacklistedCreators } from '@src/queries/influencers/useQueryCreators';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import {
  FilterBuilt,
  MediaConstraints,
  MediaFilterBuilder,
} from '@src/fragments/Filters/utils/MediaFilterBuilder';
import { ArrayHelper } from '@src/services/helpers/array';
import { DiscoverQs } from '@src/types/Discover';
import {
  filterCollectionXclusionState,
  filterCreatorCountriesXclusionState,
  filterOwnContentXclusionState,
} from '@src/pages/Discover/components/Atoms/Atoms';

/// Hook to gather and build all media constraints for the media query
export function useMediaConstraints(filters: DiscoverQs): {
  isExactSearch: boolean;
  mediaConstraints: MediaConstraints;
  filterBuilt: FilterBuilt;
  commonStore: MediaConstraints['state'];
} {
  const [exactMode] = filters.exact;
  const isExactSearch = exactMode === 'true';

  const filterOwnContentXclusion = useAtomValue(filterOwnContentXclusionState);
  const filterCollectionXclusion = useAtomValue(filterCollectionXclusionState);
  const filterCreatorCountriesXclusion = useAtomValue(filterCreatorCountriesXclusionState);

  const { data: userConfig } = useQueryUserConfig();
  const {
    data: blacklistedCreators = [],
    isSuccess: blacklistedCreatorsLoaded,
  } = useQueryBlacklistedCreators();
  const { data: groupMentionPageList } = useQueryGroupMentionPages();
  const user = userConfig?.user || null;
  const groupMentionPages = useMemo(
    () => ArrayHelper.mapToObject(groupMentionPageList || [], (p) => p.id),
    [groupMentionPageList]
  );
  const commonStore = useMemo(() => ({ user, groupMentionPages }), [user, groupMentionPages]);
  const blacklistedCreatorUsernames = useMemo(
    () => blacklistedCreatorsLoaded ? blacklistedCreators.map(({ username }) => username) : undefined,
    [blacklistedCreators, blacklistedCreatorsLoaded],
  );

  const { data: userCollections = [] } = useQueryCollections();
  const userCollectionIds = useMemo(() => userCollections.map(({ _id }) => _id), [userCollections]);
  const inputCollectionKeys = useMemo(() => XclusionUtility.getRawKeys(filters.collection), [filters.collection]);
  const includedCollectionKeys = useMemo(
    () => userCollectionIds.filter((id) => inputCollectionKeys.includes(id)),
    [inputCollectionKeys, userCollectionIds]
  );
  const collectionResults = useQueriesCollection(includedCollectionKeys, true);
  const collectionValues = collectionResults.map(({ data }) => data);
  const collectionsSignature = JSON.stringify(collectionValues);

  // useQueries returns a new results array on each render
  // this keeps mediaConstraints stable while still refreshing when loaded collection data actually changes.
  const collections = useMemo(
    () => ArrayHelper.filterNulls(collectionValues),
    [collectionsSignature]
  );

  const mediaConstraints = useMemo<MediaConstraints>(() => ({
    qs: filters,
    state: commonStore,
    isExactSearch,
    collections,
    blacklistedCreators: blacklistedCreatorUsernames,
    filterCollectionXclusion,
    filterOwnContentXclusion,
    filterCreatorCountriesXclusion,
  }), [
    filters,
    commonStore,
    isExactSearch,
    collections,
    blacklistedCreatorUsernames,
    filterCollectionXclusion,
    filterOwnContentXclusion,
    filterCreatorCountriesXclusion,
  ]);

  const filterBuilt = useMemo(
    () => MediaFilterBuilder.buildMediaFilter(mediaConstraints),
    [mediaConstraints]
  );

  return {
    isExactSearch,
    mediaConstraints,
    filterBuilt,
    commonStore,
  };
}
